// @ts-nocheck
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { ScreenPaths } from "./configs/paths";
import { Provider, useSelector } from "react-redux";

import CssBaseline from "@mui/material/CssBaseline";
import PrivateRoute from "./PrivateRoute";
import store from "@root/store/store";
import theme from "./theme";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import ReactGA from "react-ga4";
import { PublicRoute } from "./PublicRoute/PublicRoute";
import TNCPrivateRoute from "./TNCPrivateRoutes";
import DashboardLayout from "@root/layouts/DashboardLayout";
import { CircularProgress } from "@mui/material";

const LoginV2 = lazy(() => import("@screens/LoginV2/LoginV2"));
const OTP = lazy(() => import("@screens/OTP/OTPScreen"));
const WithdrawScreen = lazy(() => import("@screens/Withdraw/WithdrawScreen"));
const Transactions = lazy(() => import("@screens/Transactions/Transactions"));
const LandingScreen = lazy(() => import("@screens/Landing/LandingScreen"));
const LandingScreenUnregisteredUser = lazy(() => import("@screens/Landing/LandingScreenUnregisteredUser"));
const SupportScreen = lazy(() => import("@screens/Support/SupportScreen"));
const BalanceScreen = lazy(() => import("@screens/Balance/BalanceScreen"));
const ExpiryPage = lazy(() => import("@screens/ExpiryPage/ExpiryPage"));
const ErrorPage = lazy(() => import("@screens/ErrorPage/ErrorPage"));
const ProfileScreen = lazy(() => import("@screens/ProfileScreen"));
const ProfileScreenDesktop = lazy(() => import("@screens/ProfileScreenDesktop"));
const PaymentSetting = lazy(() => import("@screens/PaymentSetting"));
const RecurringWithdrawalNew = lazy(() => import("@screens/RecurringWithdrawalNew"));
const Summary = lazy(() => import("@screens/Summary/Summary"));
const EOI = lazy(() => import("@screens/EOI/Eoi"));
const FAQS = lazy(() => import("@screens/FAQ/FAQS"));
const TermesAndConditions = lazy(() => import("@screens/TOC/TermesAndConditions"));

const AppContent = () => {
  const token = localStorage.getItem("token");
  const { primaryColor } = useSelector(
    (state: any) => state.whiteLabellingData || {}
  );
  const redirectPath = token ? ScreenPaths.BALANCE : ScreenPaths.LOGIN_PATH;
  const loaderColor = primaryColor || "#2E5090";

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={redirectPath} />} />

        {/* Public Routes */}
        <Route
          path="/"
          element={
            <Suspense
              fallback={
                <div className="flex h-screen w-screen items-center justify-center bg-white">
                  <CircularProgress sx={{ color: loaderColor }} />
                </div>
              }
            >
              <PublicRoute />
            </Suspense>
          }
        >
          <Route path={ScreenPaths.LOGIN_PATH} element={<LoginV2 />} />
          <Route
            path={ScreenPaths.TERMS_CONDITIONS_PATH}
            element={<LandingScreen />}
          />
          <Route path={ScreenPaths.KILIMANJARO_EOI} element={<EOI />} />
          <Route path={ScreenPaths.KILIMANJARO_FAQ} element={<FAQS />} />

          <Route
            path={ScreenPaths.ACTIVATION_CODE_EXPIRY_PATH}
            element={<ExpiryPage />}
          />
          <Route path={ScreenPaths.OTP} element={<OTP />} />
        </Route>

        {/* TNC Private Routes */}
        <Route
          path="/"
          element={
            <Suspense
              fallback={
                <div className="flex h-screen w-screen items-center justify-center bg-white">
                  <CircularProgress sx={{ color: loaderColor }} />
                </div>
              }
            >
              <TNCPrivateRoute />
            </Suspense>
          }
        >
          <Route
            path={ScreenPaths.TERMS_AND_CONDITIONS_UNREGISTERED_PATH}
            element={<LandingScreenUnregisteredUser />}
          />
        </Route>

        {/* Private Routes */}
        <Route path="/" element={<PrivateRoute />}>
          <Route index element={<Navigate to={redirectPath} />} />
          <Route
            path={ScreenPaths.TOC_PAGE}
            element={
              <Suspense
                fallback={
                  <div className="flex-1 flex items-center justify-center min-h-[400px]">
                    <CircularProgress sx={{ color: loaderColor }} />
                  </div>
                }
              >
                <TermesAndConditions />
              </Suspense>
            }
          />

          {/* New dashboard layout wrapping main app pages */}
          <Route element={<DashboardLayout />}>
            <Route path={ScreenPaths.BALANCE} element={<BalanceScreen />} />
            <Route
              path={ScreenPaths.WITHDRAWAL_PATH}
              element={<WithdrawScreen />}
            />
            <Route
              path={ScreenPaths.TRANSACTIONS_PATH}
              element={<Transactions />}
            />
            <Route path={ScreenPaths.DEDUCTION_PATH} element={<Summary />} />
            <Route
              path={ScreenPaths.PROFILE_PATH}
              element={<ProfileScreenDesktop />}
            />
            <Route
              path={ScreenPaths.PROFILE_DESKTOP_PATH}
              element={<ProfileScreen />} // old path for profile
            />
            <Route
              path={ScreenPaths.PAYMENT_SETTINGS}
              element={<PaymentSetting />}
            />
            <Route
              path={ScreenPaths.RECURRING_WITHDRAWAL_NEW}
              element={<RecurringWithdrawalNew />}
            />
            <Route path={ScreenPaths.SUPPORT_PATH} element={<SupportScreen />} />
          </Route>
        </Route>

        <Route
          path="*"
          element={
            <Suspense
              fallback={
                <div className="flex h-screen w-screen items-center justify-center bg-white">
                  <CircularProgress sx={{ color: loaderColor }} />
                </div>
              }
            >
              <ErrorPage />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID || "G-3NY89W7XXB");

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Provider store={store}>
        <AppContent />
      </Provider>
    </ThemeProvider>
  );
}

export default App;
